// Owner column cell: the first owner plus a "+N" count, with every owner in a
// hover tooltip. The pencil opens one multiple-value autocomplete to add or
// remove owners (or, with several rows selected, the bulk Assign Owner dialog).
// Adding an owner who doesn't manage this interest's property asks to confirm the
// access grant first.
import React from "react";
import { Link } from "react-router-dom";
import { useLazyQuery } from "@apollo/client";
import { Box, CircularProgress, IconButton, Popover, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { InterestOwners, InterestOwnersVariables } from "types/gql-types";
import OwnerMultiEditor from "components/common/owners-editor/owner-multi-editor";
import { UNNAMED_OWNER, ownerLabel } from "components/common/owners-editor/owner-names";
import {
  toOwnerSummaries,
  useOwnerChanges,
} from "components/common/owners-editor/use-owner-changes";
import { INTEREST_OWNERS_QUERY } from "./query";
import UnassignedOwner from "./unassigned-owner";

export type OwnerCellProps = {
  interestId: string;

  /** This interest's list id, for the access-grant check when adding an owner. */
  interestListId: string;

  /** Owner names from the search index, in display-name order; nameless owners are omitted. */
  ownerNames: string[];

  /** Every owner's id, so the "+N" count includes nameless owners. */
  ownerUserIds: string[];

  /** Current user id, for the pinned "Me (Name)" option. */
  currentUserId: string;

  /** Current user display name, for the "Me (Name)" label. */
  currentUserName: string;

  /**
   * HAV-58/HAV-59 — the list id the dashboard is currently filtering on (or "all"
   * for the aggregate view). Its property group floats to the top of the picker.
   */
  activeInterestListId?: string | null;

  /**
   * M3-T02 — UI-only "who can assign" gate. When false the editable affordance is
   * absent from the DOM and the owners render read-only. Defaults to true.
   */
  canAssign?: boolean;

  /**
   * M4-T05 — the interest detail route. When provided the owners / em-dash link
   * to it; when omitted they are plain text. Applies in both editable and
   * read-only states.
   */
  detailTo?: string;

  /** When set, the pencil calls this (bulk Assign Owner dialog) instead of opening the editor. */
  onBulkAssign?: (() => void) | null;

  /** Pencil tooltip / aria-label; pass only alongside `onBulkAssign`. */
  bulkAssignLabel?: string;

  /** Names the application in an error that arrives after the row moved on. */
  applicantName?: string;
};

const pencilLabelFor = (labels: string[], bulkAssignLabel?: string) => {
  if (bulkAssignLabel) return { tooltip: bulkAssignLabel, ariaLabel: bulkAssignLabel };
  if (labels.length === 0) return { tooltip: "Add owner", ariaLabel: "Add owner" };
  return { tooltip: "Edit owners", ariaLabel: `Owners: ${labels.join(", ")}. Edit owners` };
};

const OwnerCell: React.FC<OwnerCellProps> = ({
  interestId,
  interestListId,
  ownerNames,
  ownerUserIds,
  currentUserId,
  currentUserName,
  activeInterestListId,
  canAssign = true,
  detailTo,
  onBulkAssign,
  bulkAssignLabel,
  applicantName,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  // The editor needs each owner's id paired with their name, which the index's
  // lists can't give once a nameless owner is dropped, so it loads them by id.
  const [loadOwners, { data, error, loading }] = useLazyQuery<
  InterestOwners,
  InterestOwnersVariables
  >(
    INTEREST_OWNERS_QUERY,
    { fetchPolicy: "network-only" },
  );

  // Loaded owners (plus in-cell edits) win until the table reloads this row with
  // new indexed owners (HAV-46: the row key excludes the owner, so the cell is
  // reused across a bulk change).
  const indexedKey = `${interestId}:${ownerUserIds.join(",")}|${ownerNames.join(",")}`;
  const [loadedForKey, setLoadedForKey] = React.useState<string | null>(null);
  const loadedOwners = data?.interest.owners;
  // Apollo keeps the previous result while a refetch is in flight, so only a
  // settled, successful load for the current indexed owners seeds the editor.
  const live = loadedForKey === indexedKey && !loading && !error && loadedOwners !== undefined;
  const seed = React.useMemo(
    () => (live && loadedOwners ? toOwnerSummaries(loadedOwners) : []),
    [live, loadedOwners],
  );
  const { owners, pendingUserIds, add, remove } = useOwnerChanges(interestId, seed, applicantName);

  // One label per owner. The index omits nameless owners from ownerNames and the
  // server lists them last, so they fill the tail as placeholders.
  const labels = live
    ? owners.map((owner) => ownerLabel(owner.name))
    : [
      ...ownerNames,
      ...Array<string>(Math.max(ownerUserIds.length - ownerNames.length, 0)).fill(UNNAMED_OWNER),
    ];
  const moreCount = labels.length - 1;

  const open = (anchor: HTMLElement) => {
    if (onBulkAssign) {
      onBulkAssign();
      return;
    }
    setAnchorEl(anchor);
    if (loadedForKey !== indexedKey || error) {
      setLoadedForKey(indexedKey);
      loadOwners({ variables: { interestId } });
    }
  };

  const pencilLabel = pencilLabelFor(labels, bulkAssignLabel);

  // M4-T05 — the owner reads as table text, not a blue hyperlink.
  const withDetailLink = (node: React.ReactNode) => (detailTo ? (
    <Box
      component={Link}
      to={detailTo}
      data-cy="owner-cell-detail-link"
      sx={{ color: "inherit", textDecoration: "none", display: "inline-flex", minWidth: 0 }}
    >
      {node}
    </Box>
  ) : node);

  const ownersWithDetailLink = labels.length === 0 ? withDetailLink(<UnassignedOwner />) : (
    <Tooltip
      title={labels.map((label, index) => (
        // Labels repeat (two unnamed owners), so position keys them.
        // eslint-disable-next-line react/no-array-index-key
        <Box component="span" key={index} sx={{ display: "block" }}>{label}</Box>
      ))}
    >
      <Box
        component="span"
        data-cy={`owner-cell-owners-${interestId}`}
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, minWidth: 0 }}
      >
        {withDetailLink(
          <Box
            component="span"
            sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {labels[0]}
          </Box>,
        )}
        {moreCount > 0 && (
          // Focusable so keyboard users reach the tooltip; outside the link so it
          // isn't nested in another interactive element. The label carries the
          // hidden owners for screen readers.
          <Box
            component="span"
            tabIndex={0}
            aria-label={`${moreCount} more ${moreCount === 1 ? "owner" : "owners"}: ${
              labels.slice(1).join(", ")}`}
            data-cy={`owner-cell-more-${interestId}`}
            sx={{ flexShrink: 0, color: "neutral.main" }}
          >
            {`+${moreCount}`}
          </Box>
        )}
      </Box>
    </Tooltip>
  );

  // M3-T02 — gated viewers see the owners READ-ONLY: the editable affordance is
  // absent from the DOM (not merely disabled).
  if (!canAssign) return ownersWithDetailLink;

  return (
    <>
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, maxWidth: "100%" }}>
        {ownersWithDetailLink}

        {/* The pencil is the only edit trigger; the owners link above only navigates. */}
        <Tooltip title={pencilLabel.tooltip}>
          <IconButton
            aria-label={pencilLabel.ariaLabel}
            aria-haspopup="dialog"
            data-cy="owner-cell-trigger"
            onClick={(event) => open(event.currentTarget)}
            sx={{ p: 0, flexShrink: 0 }}
          >
            <EditIcon data-cy="owner-cell-pencil" sx={{ fontSize: "0.9rem", color: "neutral.main" }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { p: 1.5, width: 320 } } }}
      >
        <Box data-cy="owner-cell-popover">
          {live && (
            <OwnerMultiEditor
              id={`owner-cell-${interestId}`}
              selectId={`owner-cell-select-${interestId}`}
              owners={owners}
              pendingUserIds={pendingUserIds}
              currentUserId={currentUserId}
              currentUserName={currentUserName}
              interestListId={interestListId}
              activeInterestListId={activeInterestListId}
              autoFocus
              onAdd={add}
              onRemove={remove}
            />
          )}
          {!live && !loading && error && (
            <Box component="p" role="alert" sx={{ m: 0, color: "error.main" }}>
              Couldn&apos;t load owners.
            </Box>
          )}
          {!live && (loading || !error) && <CircularProgress size={20} aria-label="Loading owners" />}
        </Box>
      </Popover>
    </>
  );
};

export default OwnerCell;
